'use client';

// Folding and unfolding, as one object moving rather than two swapping.
//
// The rail's whole claim is that a pill *is* the conversation, folded down —
// press it and it grows into the window. Until now that was only true in the
// comments: the pill was removed from the DOM and a 360×560 panel was inserted
// somewhere else in the row, with a scaleY on it to suggest a connection the
// browser had no idea about.
//
// A view transition is how the browser is told. Two elements carrying the same
// `view-transition-name` — one in the frame before the change, one in the frame
// after — are declared to be the same thing, and the browser interpolates
// between their two rectangles. That is the entire mechanism, and it is why the
// naming in `dock.tsx` is careful about only ever having one of each name on
// screen at a time: two elements claiming one name is not a morph, it is an
// error, and the browser abandons the whole transition.
//
// `flushSync` is not optional. The callback has to leave the DOM in its final
// state by the time it returns, and React's default is to batch a state update
// into a later frame — which the browser reads as "nothing changed", so it
// takes an identical second snapshot and animates nothing.
//
// No transition is a supported outcome, not a failure: a browser without the
// API, or someone who has asked for less movement, gets the change immediately
// and everything still works. That is why this returns null rather than a
// resolved promise — the caller can tell the difference, and one of them needs
// to (see `newborn` in dock.tsx).

import { flushSync } from 'react-dom';

/** The handle the browser gives back. Only `finished` is used here. */
type Morphing = { finished: Promise<void> };

type WithTransitions = Document & {
  startViewTransition?: (change: () => void) => Morphing;
};

export function morph(change: () => void): Morphing | null {
  const doc = document as WithTransitions;
  if (!doc.startViewTransition || matchMedia('(prefers-reduced-motion: reduce)').matches) {
    change();
    return null;
  }
  return doc.startViewTransition(() => flushSync(change));
}

/**
 * What a thread calls itself while it is moving.
 *
 * Thread keys are `thread-0`, `thread-1` and so on, so this is always a valid
 * CSS identifier without any escaping — worth keeping true if the naming in
 * `console.tsx` ever changes.
 */
export const morphName = (key: string) => `chat-${key}`;

/** The two rail controls that are also panels. Fixed names; there is one of each. */
export const HISTORY_MORPH = 'chat-history';
export const NEW_MORPH = 'chat-new';
